import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import InteractiveBookCTA from '../components/InteractiveBookCTA';
import VittaCIREPathways from '../components/VittaCIREPathways';

const CIREStudyGuide = () => {
  // Book and Course data
  const studyMaterials = {
    book: {
      title: "Printed CIRE Study Guide",
      subtitle: "Complete 700 Page Book",
      description: "Comprehensive exam-focused study guide covering all 9 CIRO syllabus elements.",
      price: "299.99",
      link: "https://www.amazon.ca/dp/B0FV3J7PJJ"
    },
    interactiveBook: {
      title: "CIRE Interactive Study Guide",
      description: "Master the CIRE exam with interactive flashcards, quizzes, and visual learning tools across all 9 elements.",
      price: "34.99",
      link: "/books/cire-interactive"
    },
    course: {
      title: "CIRE Exam Prep Course",
      price: "29.99",
      link: "https://vittafinance.mykajabi.com/offers/5Tzz73ay"
    }
  };

  const learningPath = [
    {
      phase: "Phase 1: Getting Started",
      description: "Understand the exam basics and create your study plan",
      duration: "Week 1",
      articles: [
        {
          title: "What is the CIRO Exam? Complete Beginner's Guide",
          slug: "what-is-ciro-exam-beginners-guide",
          description: "Everything you need to know about CIRO exams and getting started"
        },
        {
          title: "CIRO Exam Requirements & Eligibility Guide",
          slug: "ciro-exam-requirements-eligibility",
          description: "Registration process, prerequisites, and eligibility criteria"
        },
        {
          title: "How to Register for CIRO Exams",
          slug: "how-to-register-ciro-exam",
          description: "Step-by-step registration guide with Prometric booking tips"
        },
        {
          title: "CIRO Exam Cost & Fees Guide",
          slug: "ciro-exam-cost-fees-guide",
          description: "Complete breakdown of exam fees and study material costs"
        }
      ]
    },
    {
      phase: "Phase 2: Exam Strategy & Planning",
      description: "Develop your personalized study strategy and timeline",
      duration: "Week 1-2",
      articles: [
        {
          title: "CIRE Exam Element Weightings Study Guide",
          slug: "cire-exam-element-weightings-study-guide",
          description: "Focus your study on high-weight topics that matter most"
        },
        {
          title: "CIRO Exam Study Schedule: 30, 60, and 90 Day Plans",
          slug: "ciro-exam-study-schedule-plan",
          description: "Choose the right study timeline for your situation"
        },
        {
          title: "CIRO Exam Pass Rates & Difficulty",
          slug: "ciro-exam-pass-rates-difficulty",
          description: "Understand what to expect and how to prepare"
        },
        {
          title: "CIRO Exam Online vs In-Person Testing",
          slug: "ciro-exam-online-vs-in-person",
          description: "Testing center options and what to expect on exam day"
        }
      ]
    },
    {
      phase: "Phase 3: Core Content - KYC & Regulations",
      description: "Master the highest-weighted exam topics (~38% of exam)",
      duration: "Week 2-3",
      articles: [
        {
          title: "Know Your Client (KYC): Foundation of Every CIRE Question",
          slug: "know-your-client-kyc-cire-exam-guide",
          description: "The most critical topic - covers ~23% of the exam"
        },
        {
          title: "CIRO Regulatory Framework Guide",
          slug: "ciro-regulatory-framework-cire-exam",
          description: "Understand CIRO rules and compliance requirements"
        },
        {
          title: "Account Types: Individual, Joint, Corporate & Trust",
          slug: "cire-exam-account-types-guide",
          description: "Master different account structures and requirements"
        },
        {
          title: "Conflicts of Interest & Ethics",
          slug: "conflicts-of-interest-ethics-cire-exam",
          description: "Ethical decision-making framework and prohibited activities"
        }
      ]
    },
    {
      phase: "Phase 4: Investment Products",
      description: "Learn fixed income, equities, and managed products",
      duration: "Week 3-4",
      articles: [
        {
          title: "Bonds, Duration & Interest Rates",
          slug: "bonds-duration-interest-rates-cire-exam",
          description: "Bond pricing mechanics and interest rate sensitivity"
        },
        {
          title: "Mutual Funds & ETFs Complete Guide",
          slug: "mutual-funds-etfs-cire-exam-guide",
          description: "Understand managed products, MER, and fund types"
        },
        {
          title: "Derivatives Fundamentals: Options & Futures",
          slug: "derivatives-options-futures-cire-exam",
          description: "Calls, puts, premiums, and hedging vs speculation"
        }
      ]
    },
    {
      phase: "Phase 5: Trading & Operations",
      description: "Master trade execution, market integrity, and complaint handling",
      duration: "Week 4-5",
      articles: [
        {
          title: "Market Integrity & Trade Execution",
          slug: "market-integrity-trade-execution-cire-exam",
          description: "Order types, settlement rules, and prohibited practices"
        },
        {
          title: "Client Complaints Handling: The 90-Day Rule",
          slug: "client-complaints-handling-obsi-cire-exam",
          description: "OBSI process, arbitration, and complaint timelines"
        }
      ]
    },
    {
      phase: "Phase 6: Practice & Final Review",
      description: "Test your knowledge and prepare for exam day",
      duration: "Week 5-6",
      articles: [
        {
          title: "Free CIRO Practice Questions",
          slug: "free-ciro-practice-questions",
          description: "Test your knowledge with sample questions"
        },
        {
          title: "CIRE Exam Questions and Answers",
          slug: "cire-exam-questions-answers-practice",
          description: "Practice questions with detailed explanations"
        },
        {
          title: "How to Pass CIRE on Your First Attempt",
          slug: "cire-first-attempt-guide",
          description: "Final strategies and exam day tips"
        },
        {
          title: "Best CIRE Study Materials Reviewed",
          slug: "best-cire-study-materials",
          description: "Top books, courses, and resources"
        }
      ]
    }
  ];

  const additionalResources = [
    {
      title: "CIRE vs CSC Comparison",
      slug: "cire-vs-csc-comparison",
      description: "Which exam should you take?"
    },
    {
      title: "How Hard is the CIRO Exam?",
      slug: "ciro-exam-difficulty-how-hard",
      description: "Difficulty level and success tips"
    },
    {
      title: "CIRO Exam Dates & Schedule",
      slug: "ciro-exam-dates-schedule",
      description: "How to book your exam"
    },
    {
      title: "CIRO Exam Passing Score",
      slug: "ciro-exam-passing-score",
      description: "How CIRO determines your results"
    },
    {
      title: "Investment Career Path in Canada",
      slug: "investment-career-path-canada",
      description: "From CIRE to Senior Advisor"
    },
    {
      title: "Financial Services Career Roadmap",
      slug: "financial-services-career-roadmap-blueprint",
      description: "Complete career blueprint"
    }
  ];

  return (
    <>
      <SEO 
        title="CIRE Exam Study Guide | Free Learning Path & Resources | Vitta Finance Academy"
        description="Master the Canadian Investment Regulatory Exam (CIRE) with our structured 6-week study guide. Free learning path covering KYC, regulations, investment products, and practice questions. Pass CIRE on your first attempt."
        keywords="CIRE study guide, CIRE exam preparation, CIRE learning path, how to study for CIRE, CIRE exam topics, CIRE free resources, Canadian Investment Regulatory Exam guide, CIRE exam strategy"
      />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
        padding: '80px 20px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <span style={{
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#d4af37',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'inline-block',
            marginBottom: '20px'
          }}>
            FREE STUDY RESOURCE
          </span>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '800',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            CIRE Exam Study Guide
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            opacity: '0.9',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.7'
          }}>
            Your complete 6-week learning path to pass the Canadian Investment Regulatory Exam. 
            Follow our structured guide covering all exam topics from KYC to compliance.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/cire-exam"
              data-testid="cire-course-btn"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              View CIRE Course
            </Link>
            <Link 
              to="/books"
              data-testid="study-materials-btn"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '14px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              Get Study Materials
            </Link>
          </div>
        </div>
      </section>

      {/* Exam Overview */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '40px' }}>
            CIRE Exam at a Glance
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            {[
              { label: 'Questions', value: '110', sublabel: 'Multiple Choice' },
              { label: 'Duration', value: '2 Hours', sublabel: 'Computer-Based' },
              { label: 'Passing Score', value: 'Set by CIRO', sublabel: 'Per Sitting' },
              { label: 'Study Time', value: '4-6 Weeks', sublabel: 'Recommended' },
              { label: 'Largest Section', value: 'KYC ~23%', sublabel: '27 Questions' }
            ].map((stat, index) => (
              <div key={index} style={{
                background: 'white',
                padding: '25px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e3a8a' }}>{stat.value}</div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginTop: '5px' }}>{stat.label}</div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '3px' }}>{stat.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', color: '#1e3a8a', marginBottom: '15px' }}>
            Structured Learning Path
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '50px', maxWidth: '600px', margin: '0 auto 50px' }}>
            Follow this 6-phase study plan to systematically master all CIRE exam content
          </p>

          {learningPath.map((phase, phaseIndex) => (
            <div key={phaseIndex} style={{ marginBottom: '40px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '1.25rem',
                  flexShrink: 0
                }}>
                  {phaseIndex + 1}
                </div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', color: '#1e3a8a', margin: 0 }}>{phase.phase}</h3>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '3px 0 0' }}>
                    {phase.description} • <span style={{ color: '#d4af37', fontWeight: '600' }}>{phase.duration}</span>
                  </p>
                </div>
              </div>

              <div style={{
                marginLeft: '25px',
                borderLeft: phaseIndex < learningPath.length - 1 ? '2px solid #e5e7eb' : 'none',
                paddingLeft: '40px',
                paddingBottom: phaseIndex < learningPath.length - 1 ? '20px' : '0'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '15px'
                }}>
                  {phase.articles.map((article, articleIndex) => (
                    <Link 
                      key={articleIndex}
                      to={`/blog/${article.slug}`}
                      data-testid={`article-${article.slug}`}
                      style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        textDecoration: 'none',
                        border: '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                        display: 'block'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = '#1e3a8a';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(30, 58, 138, 0.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <h4 style={{ 
                        fontSize: '1rem', 
                        color: '#1e3a8a', 
                        margin: '0 0 8px',
                        lineHeight: '1.4'
                      }}>
                        {article.title}
                      </h4>
                      <p style={{ 
                        fontSize: '0.85rem', 
                        color: '#6b7280', 
                        margin: 0,
                        lineHeight: '1.5'
                      }}>
                        {article.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Study Pathways */}
      <VittaCIREPathways />

      {/* Additional Resources */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Additional Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px'
          }}>
            {additionalResources.map((resource, index) => (
              <Link 
                key={index}
                to={`/blog/${resource.slug}`}
                style={{
                  background: 'white',
                  padding: '18px 20px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'border-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#d4af37'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#d4af37',
                  flexShrink: 0
                }} />
                <div>
                  <div style={{ fontSize: '0.95rem', color: '#1e3a8a', fontWeight: '600' }}>{resource.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{resource.description}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Study Resources */}
      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.75rem', color: '#1e3a8a', marginBottom: '30px' }}>
            Recommended Study Resources
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '25px'
          }}>
            {/* Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #d4af37',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                📚
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.book.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.book.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.book.price}
                </span>
              </div>
              <a 
                href={studyMaterials.book.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#1e3a8a',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Buy on Amazon
              </a>
            </div>

            {/* Interactive Book */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #8b5cf6',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '20px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700'
              }}>NEW</div>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: 'white',
                fontSize: '1.5rem'
              }}>
                ⚡
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.interactiveBook.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                {studyMaterials.interactiveBook.description}
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.interactiveBook.price}
                </span>
              </div>
              <Link 
                to={studyMaterials.interactiveBook.link}
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Explore Interactive Book
              </Link>
            </div>

            {/* Course */}
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              border: '2px solid #1e3a8a',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: '#1e3a8a',
                fontSize: '1.5rem'
              }}>
                🎓
              </div>
              <h3 style={{ fontSize: '1.15rem', color: '#1e3a8a', marginBottom: '8px' }}>
                {studyMaterials.course.title}
              </h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '15px' }}>
                Video lessons, AI tutor, practice questions, mock exams, and expert instruction.
              </p>
              <div style={{
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                  CAD ${studyMaterials.course.price}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>/month</span>
              </div>
              <a 
                href={studyMaterials.course.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: '#d4af37',
                  color: '#1e3a8a',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Enroll Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Book CTA */}
      <InteractiveBookCTA />

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '15px' }}>Ready to Start Your CIRE Journey?</h2>
          <p style={{ opacity: '0.9', marginBottom: '30px', fontSize: '1.1rem' }}>
            Get comprehensive prep with our CIRE course featuring video lessons, 800+ practice questions, and full mock exams.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/cire-exam"
              data-testid="cta-cire-course"
              style={{
                background: '#d4af37',
                color: '#1e3a8a',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem'
              }}
            >
              Explore CIRE Course - $29.99/month
            </Link>
            <Link 
              to="/rse-study-guide"
              style={{
                background: 'transparent',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                border: '2px solid white'
              }}
            >
              View RSE Study Guide
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CIREStudyGuide;
